// src/pages/AdminUpload.js
import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Input,
  VStack,
  Image,
  useToast,
  Text,
  Divider,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  HStack,
  Select,
  Spinner,
  Switch,
  FormControl,
  FormLabel,
  Badge,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import {
  uploadDonation,
  uploadPhoto,
  fetchAllPledges,
  deletePledge,
  searchDonorsByFirstName,
  updateDonation,
  deleteDonation,
  uploadExpenditure,
  updateExpenditure,
  deleteExpenditure,
  fetchAllBanners,
  createBanner,
  deleteBanner,
  fetchVoteCount, // 🗳️ admin-only vote count
  fetchRevealStatus, // 🔓 current reveal flag
  setRevealStatus, // 🔓 toggle reveal
} from "../api/api";

const SAFFRON_DARK = "#854F0B";
const SAFFRON_PALE = "#FAEEDA";

const AdminUpload = () => {
  // 🔹 Donations state
  //
  // CHANGED: search is now its own field (`donorSearchQuery`),
  // separate from the edit form fields (donorFirstName/donorLastName).
  // Before, the First Name / Last Name inputs did double duty as both
  // the search box AND the edit form — confusing, since typing to
  // search would also change what you'd submit. Now:
  //   1. Type anything (first name, last name, or part of either) in
  //      ONE search bar.
  //   2. Pick a match from the dropdown.
  //   3. That fills the separate, freely-editable First Name / Last
  //      Name / Amount fields below, ready to Update or Delete.
  const [donorSearchQuery, setDonorSearchQuery] = useState("");
  const [donorFirstName, setDonorFirstName] = useState("");
  const [donorLastName, setDonorLastName] = useState("");
  const [amount, setAmount] = useState("");
  const [file, setFile] = useState(null);
  const [donors, setDonors] = useState([]);
  const [selectedDonorId, setSelectedDonorId] = useState(null);
  const [searching, setSearching] = useState(false);
  const [pledges, setPledges] = useState([]);
  const [approvingId, setApprovingId] = useState(null);

  // 🔹 Expenditures state
  const [expDescription, setExpDescription] = useState("");
  const [expPaidTo, setExpPaidTo] = useState("");
  const [expAmount, setExpAmount] = useState("");
  const [expSearch, setExpSearch] = useState("");
  const [expResults, setExpResults] = useState([]);
  const [selectedExpId, setSelectedExpId] = useState(null);
  const [searchingExp, setSearchingExp] = useState(false);
  const [expCategory, setExpCategory] = useState("");
  const [expDate, setExpDate] = useState("");

  const toast = useToast();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  // 🔹 Banner state
  const [bannerMessage, setBannerMessage] = useState("");
  const [bannerStartTime, setBannerStartTime] = useState("");
  const [bannerEndTime, setBannerEndTime] = useState("");
  const [banners, setBanners] = useState([]);
  const [loadingBanners, setLoadingBanners] = useState(false);

  // 🔹 Votes & reveal state
  const [voteCount, setVoteCount] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [loadingVotes, setLoadingVotes] = useState(false);
  const [togglingReveal, setTogglingReveal] = useState(false);

  // ✅ Load pledges
  const loadPledges = async () => {
    try {
      const res = await fetchAllPledges(token);
      setPledges(res.data);
    } catch {
      toast({ title: "Failed to load pledges", status: "error" });
    }
  };

  // ✅ Load banners
  const loadBanners = async () => {
    setLoadingBanners(true);
    try {
      const res = await fetchAllBanners(token);
      setBanners(res.data);
    } catch {
      toast({ title: "Failed to load banners", status: "error" });
    } finally {
      setLoadingBanners(false);
    }
  };

  // ✅ Load vote count + reveal status
  const loadVotesAndReveal = async () => {
    setLoadingVotes(true);
    try {
      const [voteRes, revealRes] = await Promise.all([
        fetchVoteCount(token),
        fetchRevealStatus(token),
      ]);
      setVoteCount(voteRes.data?.count ?? 0);
      setRevealed(revealRes.data?.revealed ?? false);
    } catch {
      toast({ title: "Failed to load vote data", status: "error" });
    } finally {
      setLoadingVotes(false);
    }
  };

  // ✅ Toggle reveal
  const handleToggleReveal = async () => {
    setTogglingReveal(true);
    try {
      const res = await setRevealStatus(!revealed, token);
      setRevealed(res.data?.revealed ?? false);
      toast({
        title: res.data?.revealed
          ? "Totals are now public 🎉"
          : "Totals hidden again",
        status: "success",
      });
    } catch {
      toast({ title: "Failed to update reveal status", status: "error" });
    } finally {
      setTogglingReveal(false);
    }
  };

  useEffect(() => {
    const verifySession = async () => {
      if (!token) {
        toast({
          title: "Session Expired",
          description: "Please login again.",
          status: "warning",
        });
        navigate("/admin/login");
        return;
      }

      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/auth/validate-token`,
          {
            method: "GET",
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (!res.ok) {
          throw new Error("Token invalid");
        }

        loadPledges();
        loadBanners();
        loadVotesAndReveal();
      } catch (err) {
        localStorage.removeItem("token");
        toast({
          title: "Session Expired",
          description: "Please login again.",
          status: "warning",
        });
        navigate("/admin/login");
      }
    };

    verifySession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 🔍 Auto search donors — single search bar, debounced + race-safe.
  // Matches any part of the donor's first OR last name (backend does
  // the actual combined-name matching — see /donations/search).
  const requestIdRef = useRef(0);

  useEffect(() => {
    const searchTerm = donorSearchQuery.trim();

    if (searchTerm.length < 3) {
      setDonors([]);
      return;
    }

    const debounceTimer = setTimeout(async () => {
      const thisRequestId = ++requestIdRef.current;
      setSearching(true);
      try {
        const res = await searchDonorsByFirstName(searchTerm, token);

        // Ignore stale responses from an earlier, now-outdated search.
        if (thisRequestId !== requestIdRef.current) return;

        setDonors(res.data || []);
      } catch {
        if (thisRequestId === requestIdRef.current) {
          toast({ title: "Search failed", status: "error" });
        }
      } finally {
        if (thisRequestId === requestIdRef.current) {
          setSearching(false);
        }
      }
    }, 400);

    return () => clearTimeout(debounceTimer);
  }, [donorSearchQuery, token, toast]);

  // ✅ Manual donation add
  const handleDonationSubmit = async () => {
    if (!donorFirstName || !donorLastName || !amount) {
      toast({ title: "Please enter all donor details", status: "warning" });
      return;
    }
    try {
      await uploadDonation(
        { donorFirstName, donorLastName, amount: parseFloat(amount) },
        token,
      );
      toast({ title: "Donation saved", status: "success" });
      resetForm();
    } catch (err) {
      toast({
        title: "Donation failed",
        description: err.message,
        status: "error",
      });
    }
  };

  // ✅ Update donation
  const handleUpdate = async () => {
    if (!selectedDonorId) return;
    try {
      await updateDonation(
        selectedDonorId,
        { donorFirstName, donorLastName, amount: parseFloat(amount) },
        token,
      );
      toast({ title: "Updated successfully", status: "success" });
      resetForm();
    } catch (err) {
      toast({ title: "Update failed", status: "error" });
    }
  };

  // ✅ Delete donation
  const handleDelete = async () => {
    if (!selectedDonorId) return;
    try {
      await deleteDonation(selectedDonorId, token);
      toast({ title: "Deleted successfully", status: "info" });
      resetForm();
    } catch (err) {
      toast({ title: "Delete failed", status: "error" });
    }
  };

  // ✅ Approve pledge
  const handleApprovePledge = async (pledge) => {
    if (approvingId) return;
    setApprovingId(pledge.id);
    try {
      await uploadDonation(
        {
          donorFirstName: pledge.donorFirstName,
          donorLastName: pledge.donorLastName,
          amount: pledge.amount,
        },
        token,
      );
      await deletePledge(pledge.id, token);
      toast({ title: "Pledge approved ✅", status: "success" });
      await loadPledges();
    } catch (err) {
      toast({
        title: "Approval failed",
        description: err.message,
        status: "error",
      });
    } finally {
      setApprovingId(null);
    }
  };

  const handleDeletePledge = async (pledgeId) => {
    try {
      await deletePledge(pledgeId, token);
      toast({ title: "Pledge deleted", status: "info" });
      await loadPledges();
    } catch (err) {
      toast({
        title: "Delete failed",
        description: err.message,
        status: "error",
      });
    }
  };

  // ✅ Select donor from search results — fills the editable fields.
  const handleSelectDonor = (id) => {
    const donor = donors.find((d) => d.id === id);
    if (donor) {
      setSelectedDonorId(donor.id);
      setDonorFirstName(donor.donorFirstName);
      setDonorLastName(donor.donorLastName);
      setAmount(donor.amount);
    }
  };

  // ✅ Photo upload
  const handlePhotoUpload = async () => {
    if (!file) {
      toast({ title: "Please select a file", status: "warning" });
      return;
    }
    try {
      await uploadPhoto(file, token);
      toast({ title: "Photo uploaded", status: "success" });
      setFile(null);
    } catch (err) {
      toast({
        title: "Upload failed",
        description: err.message,
        status: "error",
      });
    }
  };

  // ✅ Expenditure Add
  const handleExpenditureSubmit = async () => {
    if (
      !expDescription ||
      !expPaidTo ||
      !expAmount ||
      !expCategory ||
      !expDate
    ) {
      toast({ title: "Please fill all expenditure fields", status: "warning" });
      return;
    }

    try {
      const payload = {
        description: expDescription,
        paidTo: expPaidTo,
        amount: parseFloat(expAmount),
        category: expCategory,
        date: expDate,
      };

      await uploadExpenditure(payload, token);
      toast({ title: "Expenditure saved", status: "success" });
      resetExpForm();
    } catch (err) {
      toast({
        title: "Save failed",
        description: err.message,
        status: "error",
      });
    }
  };

  // ✅ Expenditure Update
  const handleExpenditureUpdate = async () => {
    if (!selectedExpId) {
      toast({ title: "Select an expenditure to update", status: "warning" });
      return;
    }

    try {
      const payload = {
        description: expDescription,
        paidTo: expPaidTo,
        amount: parseFloat(expAmount),
        category: expCategory,
        date: expDate,
      };

      await updateExpenditure(selectedExpId, payload, token);
      toast({ title: "Updated successfully", status: "success" });
      resetExpForm();
    } catch (err) {
      toast({ title: "Update failed", status: "error" });
    }
  };

  // ✅ Expenditure Delete
  const handleExpenditureDelete = async () => {
    if (!selectedExpId) {
      toast({ title: "Select an expenditure to delete", status: "warning" });
      return;
    }

    try {
      await deleteExpenditure(selectedExpId, token);
      toast({ title: "Deleted successfully", status: "info" });
      resetExpForm();
    } catch (err) {
      toast({ title: "Delete failed", status: "error" });
    }
  };

  const handleCreateBanner = async () => {
    if (!bannerMessage || !bannerStartTime || !bannerEndTime) {
      toast({ title: "Please fill all banner fields", status: "warning" });
      return;
    }

    try {
      await createBanner(
        {
          message: bannerMessage,
          startTime: bannerStartTime,
          endTime: bannerEndTime,
        },
        token,
      );

      toast({ title: "Banner created successfully", status: "success" });
      setBannerMessage("");
      setBannerStartTime("");
      setBannerEndTime("");
      loadBanners();
    } catch (err) {
      toast({
        title: "Failed to create banner",
        description: err.message,
        status: "error",
      });
    }
  };

  // ✅ Delete banner
  const handleDeleteBanner = async (id) => {
    try {
      await deleteBanner(id, token);
      toast({ title: "Banner deleted", status: "info" });
      loadBanners();
    } catch (err) {
      toast({
        title: "Failed to delete banner",
        description: err.message,
        status: "error",
      });
    }
  };

  // Reset forms
  const resetForm = () => {
    setDonorSearchQuery("");
    setDonorFirstName("");
    setDonorLastName("");
    setAmount("");
    setFile(null);
    setDonors([]);
    setSelectedDonorId(null);
  };

  const resetExpForm = () => {
    setExpDescription("");
    setExpPaidTo("");
    setExpAmount("");
    setSelectedExpId(null);
    setExpResults([]);
  };

  // ✅ Search expenditures
  const handleExpSearch = async () => {
    if (expSearch.trim().length < 3) {
      toast({ title: "Enter at least 3 characters", status: "warning" });
      return;
    }
    setSearchingExp(true);
    try {
      const res = await import("../api/api").then((m) => m.fetchExpenditures());
      const results = res.data.filter(
        (e) =>
          e.description.toLowerCase().includes(expSearch.toLowerCase()) ||
          e.paidTo.toLowerCase().includes(expSearch.toLowerCase()),
      );
      setExpResults(results);
    } catch {
      toast({ title: "Search failed", status: "error" });
    }
    setSearchingExp(false);
  };

  return (
    <Box p={6} maxW="1000px" mx="auto">
      {/* 🗳️ Votes & Reveal Control — admin only visibility */}
      <VStack
        spacing={4}
        mb={8}
        border="1.5px solid"
        borderColor="orange.300"
        p={5}
        borderRadius="xl"
        align="stretch"
        bg={SAFFRON_PALE}
      >
        <Text fontSize="xl" fontWeight="bold" color={SAFFRON_DARK}>
          🪔 Devotee Votes &amp; Reveal Control
        </Text>

        <HStack justify="space-between" flexWrap="wrap" spacing={4}>
          <HStack spacing={3}>
            <Text fontSize="sm" color="gray.600">
              Diyas lit (votes):
            </Text>
            {loadingVotes ? (
              <Spinner size="sm" />
            ) : (
              <Badge
                colorScheme="orange"
                fontSize="md"
                px={3}
                py={1}
                borderRadius="full"
              >
                {voteCount?.toLocaleString("en-IN") ?? "—"}
              </Badge>
            )}
          </HStack>

          <Button
            size="sm"
            variant="outline"
            colorScheme="orange"
            onClick={loadVotesAndReveal}
            isLoading={loadingVotes}
          >
            Refresh
          </Button>
        </HStack>

        <Divider borderColor="orange.200" />

        <FormControl
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <FormLabel
            htmlFor="reveal-toggle"
            mb="0"
            fontSize="sm"
            color="gray.700"
          >
            Public totals are currently{" "}
            <b style={{ color: revealed ? "#2F855A" : SAFFRON_DARK }}>
              {revealed ? "REVEALED" : "HIDDEN"}
            </b>
          </FormLabel>
          <Switch
            id="reveal-toggle"
            colorScheme="orange"
            isChecked={revealed}
            onChange={handleToggleReveal}
            isDisabled={togglingReveal}
          />
        </FormControl>

        <Text fontSize="xs" color="gray.500">
          Toggling this switch instantly changes what the public Expenditures
          page shows. When ON, real totals are sent to every visitor. When OFF,
          the server sends no numbers at all — not even blurred ones.
        </Text>
      </VStack>

      {/* 💸 Donation Form */}
      <VStack
        spacing={4}
        mb={8}
        border="1px solid #ccc"
        p={4}
        borderRadius="md"
        align="stretch"
      >
        <Text fontSize="xl" fontWeight="bold">
          Add / Update Donation
        </Text>

        {/* ── Single search bar — matches first OR last name ── */}
        <Box>
          <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>
            🔍 Search existing donor (type any part of their name)
          </Text>
          <HStack>
            <Input
              placeholder="e.g. Ravi, Kumar, or Ravi Kumar..."
              value={donorSearchQuery}
              onChange={(e) => setDonorSearchQuery(e.target.value)}
              bg="orange.50"
            />
            {searching && <Spinner size="sm" flexShrink={0} />}
          </HStack>

          {donors.length > 0 && (
            <Select
              mt={2}
              placeholder={`Select from ${donors.length} match(es)`}
              onChange={(e) => handleSelectDonor(e.target.value)}
              bg="white"
            >
              {donors.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.donorFirstName} {d.donorLastName} — ₹
                  {d.amount?.toLocaleString("en-IN")}
                </option>
              ))}
            </Select>
          )}

          {!searching &&
            donorSearchQuery.trim().length >= 3 &&
            donors.length === 0 && (
              <Text fontSize="xs" color="gray.500" mt={1}>
                No matching donors found.
              </Text>
            )}
        </Box>

        <Divider />

        {/* ── Editable fields — freely editable regardless of search ── */}
        <Text fontSize="xs" fontWeight="600" color="gray.500">
          {selectedDonorId
            ? "✏️ Editing selected donor — change any field below"
            : "Or fill in manually to add a new donation"}
        </Text>

        <Input
          placeholder="First Name"
          value={donorFirstName}
          onChange={(e) => setDonorFirstName(e.target.value)}
        />
        <Input
          placeholder="Last Name"
          value={donorLastName}
          onChange={(e) => setDonorLastName(e.target.value)}
        />
        <Input
          placeholder="Amount in INR"
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />

        <HStack>
          <Button colorScheme="green" onClick={handleDonationSubmit}>
            Save
          </Button>
          <Button
            colorScheme="yellow"
            onClick={handleUpdate}
            isDisabled={!selectedDonorId}
          >
            Update
          </Button>
          <Button
            colorScheme="red"
            onClick={handleDelete}
            isDisabled={!selectedDonorId}
          >
            Delete
          </Button>
          {selectedDonorId && (
            <Button size="sm" variant="ghost" onClick={resetForm}>
              Clear
            </Button>
          )}
        </HStack>
      </VStack>

      <Divider my={6} />

      {/* 📝 Pending Pledges Table */}
      <Box mb={8}>
        <Text fontSize="xl" fontWeight="bold" mb={2}>
          Pending Pledges
        </Text>
        {pledges.length === 0 ? (
          <Text>No pledges submitted yet.</Text>
        ) : (
          <TableContainer
            border="1px solid #ddd"
            borderRadius="md"
            maxH="350px"
            overflowY="auto"
          >
            <Table variant="striped" size="sm" colorScheme="orange">
              <Thead bg="orange.500" position="sticky" top="0" zIndex={1}>
                <Tr>
                  <Th color="white">First Name</Th>
                  <Th color="white">Last Name</Th>
                  <Th color="white">Mobile</Th>
                  <Th color="white" isNumeric>
                    Amount (₹)
                  </Th>
                  <Th color="white">Date</Th>
                  <Th color="white">Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {pledges.map((p) => (
                  <Tr key={p.id}>
                    <Td>{p.donorFirstName}</Td>
                    <Td>{p.donorLastName}</Td>
                    <Td>{p.mobile}</Td>
                    <Td isNumeric>{p.amount}</Td>
                    <Td>{p.date}</Td>
                    <Td>
                      <HStack spacing={2}>
                        <Button
                          size="sm"
                          colorScheme="green"
                          onClick={() => handleApprovePledge(p)}
                          isLoading={approvingId === p.id}
                        >
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          colorScheme="red"
                          variant="outline"
                          onClick={() => handleDeletePledge(p.id)}
                        >
                          Delete
                        </Button>
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <Divider my={6} />
      {/* 🖋️ Banner Management */}
      <VStack
        spacing={4}
        border="1px solid #ccc"
        p={4}
        borderRadius="md"
        mb={6}
        align="stretch"
      >
        <Text fontSize="xl" fontWeight="bold">
          Manage Banners
        </Text>

        <Input
          placeholder="Banner Message"
          value={bannerMessage}
          onChange={(e) => setBannerMessage(e.target.value)}
        />
        <Input
          type="datetime-local"
          value={bannerStartTime}
          onChange={(e) => setBannerStartTime(e.target.value)}
        />
        <Input
          type="datetime-local"
          value={bannerEndTime}
          onChange={(e) => setBannerEndTime(e.target.value)}
        />

        <Button colorScheme="green" onClick={handleCreateBanner}>
          Create Banner
        </Button>

        <Divider />

        <Text fontWeight="bold">Active Banners</Text>
        {loadingBanners ? (
          <Spinner />
        ) : banners.length === 0 ? (
          <Text>No banners found</Text>
        ) : (
          <TableContainer
            border="1px solid #ddd"
            borderRadius="md"
            maxH="300px"
            overflowY="auto"
          >
            <Table variant="simple" size="sm">
              <Thead bg="blue.500" position="sticky" top="0" zIndex={1}>
                <Tr>
                  <Th color="white">Message</Th>
                  <Th color="white">Start</Th>
                  <Th color="white">End</Th>
                  <Th color="white">Action</Th>
                </Tr>
              </Thead>
              <Tbody>
                {banners.map((b) => (
                  <Tr key={b.id}>
                    <Td>{b.message}</Td>
                    <Td>{new Date(b.startTime).toLocaleString()}</Td>
                    <Td>{new Date(b.endTime).toLocaleString()}</Td>
                    <Td>
                      <Button
                        size="sm"
                        colorScheme="red"
                        onClick={() => handleDeleteBanner(b.id)}
                      >
                        Delete
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </VStack>

      {/* 🖼️ Photo Upload */}
      <VStack
        spacing={4}
        border="1px solid #ccc"
        p={4}
        borderRadius="md"
        mb={6}
      >
        <Text fontSize="xl" fontWeight="bold">
          Upload Construction Photo
        </Text>
        <Input type="file" onChange={(e) => setFile(e.target.files[0])} />
        {file && (
          <Image
            src={URL.createObjectURL(file)}
            boxSize="200px"
            objectFit="cover"
          />
        )}
        <Button colorScheme="orange" onClick={handlePhotoUpload}>
          Upload Photo
        </Button>
      </VStack>

      <Divider my={6} />

      {/* 🏗️ Expenditures Section */}
      <Box>
        <Text fontSize="xl" fontWeight="bold" mb={4}>
          Manage Expenditures
        </Text>

        <Input
          placeholder="Search Expenditures by Description / Paid To"
          value={expSearch}
          onChange={(e) => setExpSearch(e.target.value)}
          mb={2}
        />
        <Button
          size="sm"
          colorScheme="teal"
          onClick={handleExpSearch}
          isLoading={searchingExp}
        >
          Search
        </Button>

        {expResults.length > 0 && (
          <Select
            mt={2}
            placeholder="Select expenditure"
            onChange={(e) => {
              const exp = expResults.find((x) => x.id === e.target.value);
              if (exp) {
                setSelectedExpId(exp.id);
                setExpDescription(exp.description);
                setExpPaidTo(exp.paidTo);
                setExpAmount(exp.amount);
                setExpCategory(exp.category || "");
                setExpDate(exp.date || "");
              }
            }}
          >
            {expResults.map((exp) => (
              <option key={exp.id} value={exp.id}>
                {exp.description} — ₹{exp.amount}
              </option>
            ))}
          </Select>
        )}

        <VStack spacing={3} mt={4} align="stretch">
          <Input
            placeholder="Description"
            value={expDescription}
            onChange={(e) => setExpDescription(e.target.value)}
          />
          <Input
            placeholder="Paid To"
            value={expPaidTo}
            onChange={(e) => setExpPaidTo(e.target.value)}
          />
          <Input
            placeholder="Mode"
            value={expCategory}
            onChange={(e) => setExpCategory(e.target.value)}
          />
          <Input
            placeholder="Amount in INR"
            type="number"
            value={expAmount}
            onChange={(e) => setExpAmount(e.target.value)}
          />
          <Input
            type="date"
            value={expDate}
            onChange={(e) => setExpDate(e.target.value)}
          />

          <HStack>
            <Button colorScheme="green" onClick={handleExpenditureSubmit}>
              Save
            </Button>
            <Button
              colorScheme="yellow"
              onClick={handleExpenditureUpdate}
              isDisabled={!selectedExpId}
            >
              Update
            </Button>
            <Button
              colorScheme="red"
              onClick={handleExpenditureDelete}
              isDisabled={!selectedExpId}
            >
              Delete
            </Button>
          </HStack>
        </VStack>
      </Box>
    </Box>
  );
};

export default AdminUpload;
